/**
 * @fileoverview Modal dialog for importing text data from paste or file selection.
 * @module ui/ImportDialog
 */

import { DEBUG_CBM } from '../constants.js';
import { BaseDialog } from './BaseDialog.js';


/**
 * Modal dialog helper class for importing text data.
 * Supports both direct text paste and file selection with preview before import.
 * @class
 * @extends BaseDialog
 */
export class ImportDialog extends BaseDialog {
    /**
     * Creates a new ImportDialog instance.
     * @param {Object} options - Configuration options
     * @param {string} [options.title='Import Data'] - Modal title
     * @param {string} [options.infoText=null] - Optional HTML text to display at top of dialog
     * @param {string} [options.placeholder='Paste text here or use the file browser above...'] - Textarea placeholder
     * @param {string} [options.acceptFileTypes='.txt,.json,.csv'] - Accepted file types for file input
     * @param {string} [options.importButtonText='Import'] - Text for the import button
     * @param {string} [options.cancelButtonText='Cancel'] - Text for the cancel button
     * @param {Function} [options.contentParser=null] - Optional content parser function (text) => Promise<string|{message: string, isError: boolean}>
     *   returning diagnostic info; a plain string is shown as an error only when it starts with "ERROR"
     * @param {Function} [options.getPdfLeads=null] - Optional async callback that returns lead text extracted from a loaded PDF
     */
    constructor(options = {}) {
        super({ idPrefix: 'importDialog' });

        this.options = {
            title: options.title || 'Import Data',
            infoText: options.infoText || null,
            placeholder: options.placeholder || 'Paste text here or use the file browser above.  Note you can paste in the text of a casebook case to automatically extract all leads...',
            acceptFileTypes: options.acceptFileTypes || '.txt,.json,.csv',
            importButtonText: options.importButtonText || 'Import',
            cancelButtonText: options.cancelButtonText || 'Cancel',
            contentParser: options.contentParser || null,
            getPdfLeads: options.getPdfLeads || null,
            flagSupportsImportLeadsFromPdf: options.flagSupportsImportLeadsFromPdf || false
        };
    }

    /**
     * Returns the HTML for the import modal.
     * Uses buildModalHtml template from BaseDialog.
     * @returns {string} Modal HTML string
     * @override
     */
    getModalHtml() {
        const bodyHtml = `
            <div class="import-dialog-content">
                <div class="">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <div class="input-group" style="width: auto; flex: 0 1 auto;">
                            <input
                                type="file"
                                class="form-control form-control-sm"
                                id="${this.modalId}_fileInput"
                                accept="${this.options.acceptFileTypes}"
                                style="max-width: 250px;"
                            >
                        </div>
                        ${(this.options.flagSupportsImportLeadsFromPdf && this.options.getPdfLeads) ? `<button type="button" class="btn btn-sm btn-outline-secondary" id="${this.modalId}_pdfLeadsBtn">Or: import leads from loaded PDF</button>` : ''}
                    </div>
                    <textarea
                        class="form-control import-dialog-textarea"
                        id="${this.modalId}_textarea"
                        rows="8"
                        placeholder="${this.options.placeholder}"
                    ></textarea>
                </div>
                <div class="mb-3">
                    <label for="${this.modalId}_diagnosticTextarea" class="mb-1 form-label">
                        Validation Report:
                    </label>
                    <textarea
                        class="form-control import-dialog-textarea"
                        id="${this.modalId}_diagnosticTextarea"
                        rows="6"
                        readonly
                        placeholder="Parsing diagnostics will appear here..."
                    ></textarea>
                </div>
            </div>
        `;

        const footerHtml = `
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                ${this.options.cancelButtonText}
            </button>
            <button type="button" class="btn btn-primary" id="${this.modalId}_importBtn">
                <i class="bi bi-check-lg"></i> ${this.options.importButtonText}
            </button>
        `;

        return this.buildModalHtml({
            title: this.options.title,
            icon: 'bi-box-arrow-in-down',
            bodyHtml,
            footerHtml,
            infoText: this.options.infoText
        });
    }

    /**
     * Sets up event listeners for modal interactions.
     * @override
     */
    setupEventListeners() {
        const textarea = this.getElement('_textarea');
        const diagnosticTextarea = this.getElement('_diagnosticTextarea');
        const fileInput = this.getElement('_fileInput');
        const importBtn = this.getElement('_importBtn');

        // File input change - read file and populate textarea
        fileInput.addEventListener('change', async (e) => {
            const file = e.target.files[0];
            if (file) {
                try {
                    const text = await this.readFile(file);
                    textarea.value = text;
                    await this.runContentParser(text, diagnosticTextarea);
                } catch (error) {
                    this.setDiagnosticText(`ERROR: Could not read file: ${error.message}`, diagnosticTextarea, true);
                }
            }
        });

        // Textarea input change - run content parser on user input
        textarea.addEventListener('input', async () => {
            await this.runContentParser(textarea.value, diagnosticTextarea);
        });

        // PDF leads button
        if (this.options.flagSupportsImportLeadsFromPdf && this.options.getPdfLeads) {
            const pdfLeadsBtn = this.getElement('_pdfLeadsBtn');
            pdfLeadsBtn.addEventListener('click', async () => {
                try {
                    const text = await this.options.getPdfLeads();
                    if (text) {
                        textarea.value = text;
                        await this.runContentParser(text, diagnosticTextarea);
                    }
                } catch (error) {
                    this.setDiagnosticText(`ERROR: Could not get PDF leads: ${error.message}`, diagnosticTextarea, true);
                }
            });
        }

        // Import button
        importBtn.addEventListener('click', () => {
            const text = textarea.value.trim();
            this.resolve(text || null);
        });
    }

    /**
     * Reads file content as text.
     * @param {File} file - The file to read
     * @returns {Promise<string>} Promise resolving to file content as text
     * @private
     */
    async readFile(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = (e) => reject(new Error('Failed to read file'));
            reader.readAsText(file);
        });
    }

    /**
     * Runs the content parser on the given text and displays results in the diagnostic textarea.
     * @param {string} text - The text content to parse
     * @param {HTMLTextAreaElement} diagnosticTextarea - The textarea to display diagnostic output
     * @private
     */
    async runContentParser(text, diagnosticTextarea) {
        if (!this.options.contentParser) {
            this.setDiagnosticText('ERROR: NO CONTENT PARSER FOUND', diagnosticTextarea, true);
            return;
        }

        try {
            const diagnosticOutput = await this.options.contentParser(text);
            if (diagnosticOutput && typeof diagnosticOutput === 'object') {
                // structured form: the parser says outright whether this is a failure
                this.setDiagnosticText(diagnosticOutput.message || '', diagnosticTextarea, !!diagnosticOutput.isError);
            } else {
                this.setDiagnosticText(diagnosticOutput || '', diagnosticTextarea);
            }
        } catch (error) {
            if (DEBUG_CBM) {
                console.debug('DEBUG: runContentParser error parsing content:', error);
            }
            this.setDiagnosticText(`ERROR: Failed to parse content: ${error.message}`, diagnosticTextarea, true);
        }
    }


    /**
     * Sets the diagnostic textarea text and applies error styling when the report is a failure.
     * A successful report may well contain the word "error" (it lists the skipped items and why),
     * so the styling follows the explicit flag when given, else whether the message STARTS with
     * "ERROR", which is how every failure string in the dialog and its parsers begins.
     * @param {string} msg - The diagnostic message to display
     * @param {HTMLTextAreaElement} el - The diagnostic textarea element
     * @param {boolean} [isError] - Whether the report is a failure; omitted = infer from the prefix
     * @private
     */
    setDiagnosticText(msg, el, isError) {
        el.value = msg;
        if (isError === undefined) {
            isError = /^\s*error\b/i.test(msg || '');
        }
        if (isError) {
            el.classList.add("text-danger");
        } else {
            el.classList.remove("text-danger");
        }
    }

    /**
     * Shows the import dialog (singleton - only one can be open at a time).
     * @param {Object} options - Configuration options (see constructor)
     * @returns {Promise<string|null>} Imported text, or null if cancelled
     */
    static async showImportDialog(options = {}) {
        return BaseDialog.showSingleton(ImportDialog, options);
    }
}
